import React, { useRef, useEffect, useState } from 'react';
import styles from './MarqueeText.module.scss';

interface MarqueeTextProps {
  /** 文本内容 */
  text: string;
  /** 是否获焦 */
  focused: boolean;
  /** 文本类名 */
  className?: string;
  /** 延迟开始时间（毫秒），默认 500ms */
  delay?: number;
  /** 滚动速度（像素/秒），默认 50 */
  speed?: number;
  /** 文本间隔距离（像素），默认 50 */
  gap?: number;
}

/**
 * 跑马灯文本组件
 * 当文本超长且获焦时，自动实现无缝滚动效果
 * 未获焦时显示省略号
 */
export const MarqueeText: React.FC<MarqueeTextProps> = ({
  text,
  focused,
  className = '',
  delay = 500,
  speed = 50,
  gap = 50,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const animationRef = useRef<number | null>(null);

  // 检测文本是否溢出
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && wrapperRef.current) {
        const firstChild = wrapperRef.current.firstElementChild as HTMLElement;
        if (firstChild) {
          const containerWidth = containerRef.current.offsetWidth;

          // 临时移除省略号样式以获取真实文本宽度
          const originalDisplay = firstChild.style.display;
          const originalMaxWidth = firstChild.style.maxWidth;
          const originalOverflow = firstChild.style.overflow;
          const originalTextOverflow = firstChild.style.textOverflow;
          const originalWhiteSpace = firstChild.style.whiteSpace;

          firstChild.style.display = 'inline-block';
          firstChild.style.maxWidth = 'none';
          firstChild.style.overflow = 'visible';
          firstChild.style.textOverflow = 'clip';
          firstChild.style.whiteSpace = 'nowrap';

          const textWidth = firstChild.scrollWidth;

          // 恢复原始样式
          firstChild.style.display = originalDisplay;
          firstChild.style.maxWidth = originalMaxWidth;
          firstChild.style.overflow = originalOverflow;
          firstChild.style.textOverflow = originalTextOverflow;
          firstChild.style.whiteSpace = originalWhiteSpace;

          const overflow = textWidth > containerWidth;
          setIsOverflow(overflow);
        }
      }
    };

    // 立即检测一次
    checkOverflow();

    // 延迟检测，确保 DOM 已完全渲染
    const timer = setTimeout(checkOverflow, 50);

    // 监听窗口大小变化
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text]);

  // 跑马灯动画逻辑
  useEffect(() => {
    if (!focused || !isOverflow) {
      // 未获焦或未溢出：重置位置
      if (wrapperRef.current) {
        wrapperRef.current.style.transform = 'translateX(0)';
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    // 延迟后开始动画
    const delayTimer = setTimeout(() => {
      const container = containerRef.current;
      const wrapper = wrapperRef.current;
      if (!container || !wrapper) return;

      const firstChild = wrapper.firstElementChild as HTMLElement;
      if (!firstChild) return;

      // 单个文本的宽度 + 间隔
      const textWidth = firstChild.offsetWidth;
      const scrollDistance = textWidth + gap;

      // 计算动画持续时间（毫秒）
      const duration = (scrollDistance / speed) * 1000;

      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;

        // 计算当前应该滚动的距离
        const progress = (elapsed % duration) / duration;
        const translateX = -scrollDistance * progress;

        wrapper.style.transform = `translateX(${translateX}px)`;

        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(delayTimer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [focused, isOverflow, delay, speed, gap]);

  return (
    <div
      ref={containerRef}
      className={`${styles.marqueeContainer} ${className} ${focused ? styles.focused : styles.unfocused}`}
    >
      {focused && isOverflow ? (
        // 获焦且溢出：显示双份文本实现无缝滚动
        <div ref={wrapperRef} className={styles.marqueeWrapper}>
          <span className={styles.marqueeText}>{text}</span>
          <span className={styles.marqueeText} style={{ marginLeft: `${gap}px` }}>
            {text}
          </span>
        </div>
      ) : (
        // 未获焦或未溢出：显示单份文本
        <div ref={wrapperRef} className={styles.marqueeWrapper}>
          <span className={styles.marqueeText}>{text}</span>
        </div>
      )}
    </div>
  );
};
